import { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';

// Curated Unsplash photo IDs from picsum.photos — landscapes, architecture, nature
const PHOTO_POOL = [
  { id: '10',  alt: 'Mountain aerial' },
  { id: '11',  alt: 'Ocean waves' },
  { id: '13',  alt: 'City skyline' },
  { id: '15',  alt: 'Forest path' },
  { id: '17',  alt: 'Mountain road' },
  { id: '20',  alt: 'Suspension bridge' },
  { id: '25',  alt: 'Mossy forest' },
  { id: '27',  alt: 'Coastal cliffs' },
  { id: '29',  alt: 'Mountain lake' },
  { id: '32',  alt: 'Autumn forest' },
  { id: '37',  alt: 'Rocky coast' },
  { id: '43',  alt: 'Waterfall' },
  { id: '50',  alt: 'Alpine meadow' },
  { id: '57',  alt: 'Pine forest' },
  { id: '91',  alt: 'Misty forest' },
  { id: '96',  alt: 'Woodland trail' },
  { id: '101', alt: 'Sandy beach' },
  { id: '119', alt: 'Forest light' },
];

const TILE_COUNT = 6;
const INTERVAL_MS = 15_000;
const FADE_MS = 700;

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function pickReplacement(usedIds: Set<string>): typeof PHOTO_POOL[number] {
  const available = PHOTO_POOL.filter(p => !usedIds.has(p.id));
  return available[Math.floor(Math.random() * available.length)];
}

interface Props {
  onSignIn: () => void;
}

export function LandingPage({ onSignIn }: Props) {
  const [photos, setPhotos] = useState(() => shuffle(PHOTO_POOL).slice(0, TILE_COUNT));
  const [fadingTile, setFadingTile] = useState<number | null>(null);
  const tileCounter = useRef(0);
  const swapTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    // Randomise starting tile so the sequence isn't always tile 0 first
    tileCounter.current = Math.floor(Math.random() * TILE_COUNT);

    const interval = setInterval(() => {
      const tileIdx = tileCounter.current % TILE_COUNT;
      tileCounter.current += 1;

      setFadingTile(tileIdx);

      swapTimeout.current = setTimeout(() => {
        setPhotos(prev => {
          const usedIds = new Set(prev.filter((_, i) => i !== tileIdx).map(p => p.id));
          const next = [...prev];
          next[tileIdx] = pickReplacement(usedIds);
          return next;
        });
        setFadingTile(null);
      }, FADE_MS);
    }, INTERVAL_MS);

    return () => {
      clearInterval(interval);
      clearTimeout(swapTimeout.current);
    };
  }, []);

  return (
    <div style={styles.root}>
      {/* Mosaic background */}
      <div style={styles.mosaic}>
        {photos.map((photo, i) => (
          <div key={i} style={styles.tile}>
            <img
              src={`https://picsum.photos/id/${photo.id}/900/600`}
              alt={photo.alt}
              style={{
                ...styles.img,
                opacity: fadingTile === i ? 0 : 1,
                transition: `opacity ${FADE_MS}ms ease`,
              }}
            />
          </div>
        ))}
      </div>

      {/* Overlay */}
      <div style={styles.overlay} />

      {/* Sign-in card */}
      <div style={styles.card}>
        <div style={styles.iconRing}>
          <i className="pi pi-bolt" style={{ fontSize: '1.75rem', color: '#6366f1' }} />
        </div>
        <h1 style={styles.title}>Templater</h1>
        <p style={styles.subtitle}>Sign in or leave.</p>
        <Button
          label="Sign in"
          icon="pi pi-sign-in"
          onClick={onSignIn}
          style={{ width: '100%', justifyContent: 'center' }}
        />
        <p style={styles.credit}>
          Photos via{' '}
          <a href="https://unsplash.com" target="_blank" rel="noopener noreferrer" style={styles.link}>
            Unsplash
          </a>
        </p>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  root: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  mosaic: {
    position: 'absolute',
    inset: 0,
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gridTemplateRows: 'repeat(2, 1fr)',
    gap: '3px',
  },
  tile: {
    overflow: 'hidden',
  },
  img: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(135deg, rgba(15,23,42,0.72) 0%, rgba(30,41,59,0.60) 100%)',
  },
  card: {
    position: 'relative',
    background: 'rgba(255,255,255,0.97)',
    borderRadius: '1.25rem',
    padding: '2.5rem 2rem',
    width: '320px',
    boxShadow: '0 25px 60px rgba(0,0,0,0.35)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem',
    textAlign: 'center',
  },
  iconRing: {
    width: '4rem',
    height: '4rem',
    borderRadius: '50%',
    background: '#eef2ff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '0.25rem',
  },
  title: {
    margin: 0,
    fontSize: '1.4rem',
    fontWeight: 700,
    color: '#1e293b',
    letterSpacing: '-0.01em',
  },
  subtitle: {
    margin: 0,
    fontSize: '0.875rem',
    color: '#64748b',
    lineHeight: 1.6,
  },
  credit: {
    margin: 0,
    fontSize: '0.72rem',
    color: '#94a3b8',
  },
  link: {
    color: '#94a3b8',
    textDecoration: 'underline',
  },
};
